import { AxiosError } from 'axios';
import { useField } from 'formik';
import React, { useEffect, useState } from 'react';
import { Input } from 'semantic-ui-react';

import AdminApi from 'api/AdminApi';
import * as dto from 'client/dto';
import { useDebouncedState } from 'hooks/useDebouncedState';

import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { SemanticFormikProps } from 'components/SidebarView/SettingsView/FormikControls/FormikSemanticProps';

type FormikSemanticUserAdminUsernameInputProps = SemanticFormikProps & {
  onUsernameConflictingChanged?: (isValid: boolean) => void;
  onUserModelCheckResultChanged?: (model: dto.UserModel) => void;
};

export const FormikSemanticUserAdminUsernameInput: React.FC<
  FormikSemanticUserAdminUsernameInputProps
> = ({
  name,
  onUsernameConflictingChanged,
  onUserModelCheckResultChanged,
  placeholder,
  error,
  disabled = false,
}) => {
  const [field, , helpers] = useField(name);
  const { value, onBlur } = field;
  const { setValue } = helpers;

  const [isLoading, setIsLoading] = useState(false);
  const [isUsernameConflicting, setIsUsernameConflicting] = useState(true);
  const [userModelIfExistsOnAuth, setUserModelIfExistsOnAuth] = useState<dto.UserModel>(null);

  const [, searchTextDebounced, setSearchText] = useDebouncedState(value || '', 500);

  const handleOnChange = (newVal: string) => {
    setValue(newVal);
    setSearchText(newVal);
  };

  useEffect(() => {
    if (disabled) {
      return;
    }

    if (searchTextDebounced && searchTextDebounced.length) {
      setIsLoading(true);
      AdminApi.checkUserName(searchTextDebounced)
        .then(result => {
          setIsUsernameConflicting(true);
          setUserModelIfExistsOnAuth(result);
        })
        .catch((resultError: AxiosError) => {
          if (resultError.response.status === 409) {
            setIsUsernameConflicting(false);
            setUserModelIfExistsOnAuth(null);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsUsernameConflicting(true);
      setUserModelIfExistsOnAuth(null);
    }
  }, [searchTextDebounced]);

  useEffect(() => {
    onUsernameConflictingChanged && onUsernameConflictingChanged(isUsernameConflicting);
  }, [isUsernameConflicting]);

  useEffect(() => {
    onUserModelCheckResultChanged && onUserModelCheckResultChanged(userModelIfExistsOnAuth);
  }, [userModelIfExistsOnAuth]);

  return (
    <FlexGroup
      suffix={
        !isUsernameConflicting && (
          <Typography variant="body2" color="clay100">
            User already exists!
          </Typography>
        )
      }
    >
      <Input
        fluid
        placeholder={placeholder}
        name={name}
        error={error || !isUsernameConflicting}
        disabled={disabled}
        onChange={(_, { value }) => handleOnChange(value)}
        onBlur={onBlur}
        defaultValue={value}
        loading={isLoading}
      />
    </FlexGroup>
  );
};
